import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  Sun,
  Moon,
  UserCircle,
  LogOut,
  X,
  HardDrive,
  RefreshCw,
  MoreHorizontal,
  Volume2,
  Globe,
  Info,
  Bell,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { FaGlobe } from "react-icons/fa";
import Tooltip from "@material-ui/core/Tooltip";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
} from "@material-ui/core";
import NotificationsIcon from "@material-ui/icons/Notifications";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../../components/NotificationsPopOver";
import NotificationsVolume from "../../components/NotificationsVolume";
import UserModal from "../../components/UserModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import BackdropLoading from "../../components/BackdropLoading";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import AnnouncementsPopover from "../../components/AnnouncementsPopover";
import BirthdayModal from "../../components/BirthdayModal";
import ChatPopover from "../../pages/Chat/ChatPopover";
import ColorModeContext from "../themeContext";
import { getBackendUrl } from "../../config";
import Logo from "../../components/Logo";
import IconButtonWithTooltip from "../../components/ui/IconButtonWithTooltip";
import VersionControl from "../../components/VersionControl";
import useSocketListener from "../../hooks/useSocketListener";
import useSettings from "../../hooks/useSettings";
import { useDate } from "../../hooks/useDate";
import api from "../../services/api";
import DiskQuotaLimitModal from "../../components/DiskQuotaLimitModal";
import DiskCleanupProgress from "../../components/DiskCleanupProgress";
import "./navbar.css";

const packageVersion = require("../../../package.json").version;
const backendUrl = getBackendUrl();

const ModernLayout = ({ children, themeToggle, hideMenu = false }) => {
  const location = useLocation();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileMoreMenuOpen, setMobileMoreMenuOpen] = useState(false);
  const { handleLogout, loading, user, socket } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);
  const { colorMode } = useContext(ColorModeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [diskUsage, setDiskUsage] = useState(null);
  const [warningThreshold, setWarningThreshold] = useState(90); // Padrão 90%
  const [showDiskQuotaModal, setShowDiskQuotaModal] = useState(false);
  const [showCleanupProgress, setShowCleanupProgress] = useState(false);
  const [hasShownWarning, setHasShownWarning] = useState(false); // Para não mostrar múltiplas vezes
  const mobileMoreMenuRef = useRef(null);
  const desktopUtilityRef = useRef(null);
  const [desktopUtilityOpen, setDesktopUtilityOpen] = useState(false);
  const cleanupCloseTimeoutRef = useRef(null);

  // Funcionalidades do layout clássico
  const [showOptions, setShowOptions] = useState(false);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [updateInProgress, setUpdateInProgress] = useState(false);
  const [changelogModalOpen, setChangelogModalOpen] = useState(false);
  const [changelogUrl, setChangelogUrl] = useState("");
  
  // Link padrão do Google Docs com as atualizações do sistema
  const DEFAULT_CHANGELOG_URL = "https://docs.google.com/document/d/16DA0bMPFdxjpHLzXtLsxxm0oSiEzeA52-GGbNYLETvI/edit?usp=sharing";

  // Função para converter link do Google Docs para formato embed
  const getEmbedUrl = (url) => {
    if (!url) return DEFAULT_CHANGELOG_URL.replace("/edit?usp=sharing", "/preview");
    // Se já é um link embed/preview, retorna como está
    if (url.includes("/preview") || url.includes("/pub?embedded=true") || url.includes("/pub?")) {
      return url;
    }
    // Converte link normal do Google Docs para preview (funciona melhor no iframe)
    if (url.includes("/edit")) {
      return url.replace("/edit?usp=sharing", "/preview").replace("/edit", "/preview");
    }
    // Adiciona /preview se não tiver
    if (url.includes("docs.google.com/document/d/")) {
      const docId = url.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1];
      if (docId) {
        return `https://docs.google.com/document/d/${docId}/preview`;
      }
    }
    return url;
  };


  const [volume, setVolume] = useState(() => {
    const savedVolume = localStorage.getItem("volume");
    if (savedVolume === null) return 1;
    const parsedVolume = Number(savedVolume);
    return isNaN(parsedVolume) ? 1 : Math.max(0, Math.min(1, parsedVolume));
  });

  useEffect(() => {
    localStorage.setItem("volume", volume);
  }, [volume]);

  const [profileUrl, setProfileUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const userMenuRef = useRef(null);
  const avatarRef = useRef(null);
  const languageRef = useRef(null);

  // Função para obter iniciais do nome
  const getInitials = (name) => {
    if (!name) return "U";
    const names = name.trim().split(" ");
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const isDarkMode = theme.palette?.type === "dark" || colorMode?.mode === "dark";

  // Configurações de idioma
  const LANGUAGE_OPTIONS = [
    { code: "pt-BR", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "ar", label: "عربي" },
  ];

  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const { getAll, getGlobalSetting } = useSettings();

  useEffect(() => {
    async function fetchSettings() {
      try {
        const settings = await getAll();
        const enabledLanguagesSetting = settings.find(
          (s) => s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt-BR", "en"];
        try {
          if (enabledLanguagesSetting) {
            langs = JSON.parse(enabledLanguagesSetting);
          }
        } catch { }
        setEnabledLanguages(langs);
        
        // Buscar changelogUrl da company master (1) usando endpoint global
        try {
          const changelogUrlFromMaster = await getGlobalSetting("changelogUrl");
          setChangelogUrl(changelogUrlFromMaster || DEFAULT_CHANGELOG_URL);
        } catch (err) {
          console.log("Layout - erro ao carregar changelogUrl:", err);
          setChangelogUrl(DEFAULT_CHANGELOG_URL);
        }
      } catch (error) {
        console.log("Layout - erro ao carregar settings:", error);
      }
    }
    fetchSettings();
  }, [user?.companyId, getAll, getGlobalSetting]);

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    enabledLanguages.includes(lang.code)
  );

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.location.reload();
  };

  // Buscar informativos
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const { data } = await api.get("/announcements/for-company", {
          params: {
            status: true,
            pageNumber: "1"
          }
        });

        const activeAnnouncements = data.records.filter(announcement => {
          const isActive = announcement.status === true || announcement.status === "true";
          const isNotExpired = !announcement.expiresAt || new Date(announcement.expiresAt) > new Date();
          return isActive && isNotExpired;
        });

        setAnnouncements(activeAnnouncements);

        if (activeAnnouncements.length > 0) {
          setShowAnnouncementsModal(true);
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (user?.id) {
      fetchAnnouncements();
    }
  }, [user?.id]);

  // Profile URL
  useEffect(() => {
    const companyId = user?.companyId;
    if (companyId) {
      const buildProfileUrl = () => {
        const savedProfileImage = localStorage.getItem("profileImage");
        const currentProfileImage = savedProfileImage || user.profileImage;

        if (currentProfileImage) {
          return `${backendUrl}/public/company${companyId}/user/${currentProfileImage}`;
        }
        return `${backendUrl}/public/app/noimage.png`;
      };

      setProfileUrl(buildProfileUrl());
    }
  }, [user?.companyId, user?.profileImage]);

  // Socket listeners callbacks
  const handleAuthEvent = useCallback((data) => {
    if (data.user.id === +user?.id) {
      toastError("Sua conta foi acessada em outro computador.");
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 1000);
    }
  }, [user?.id]);

  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        localStorage.setItem("profileImage", data.user.profileImage);
      }
    }
  }, [user?.companyId, user?.id]);

  const handleUserBirthday = useCallback((data) => {
    console.log("🎂 Evento de aniversário de usuário recebido:", data);
    if (data.userId === +user?.id) {
      setShowBirthdayModal(true);
    }
  }, [user?.id]);

  const handleContactBirthday = useCallback((data) => {
    console.log("🎂 Evento de aniversário de contato recebido:", data);
  }, []);

  const checkBirthdaysOnLogin = useCallback(async () => {
    if (user?.id && user?.companyId) {
      try {
        const { data } = await api.get("/birthdays/today");
        const birthdayData = data.data;

        const userBirthday = birthdayData.users.find(u => u.id === +user.id);
        if (userBirthday) {
          console.log("🎂 Usuário faz aniversário hoje! Mostrando modal...");
          setShowBirthdayModal(true);
        }

        if (birthdayData.users.length > 0 || birthdayData.contacts.length > 0) {
          console.log("🎂 Há aniversariantes hoje:", birthdayData);
        }
      } catch (error) {
        console.error("Erro ao verificar aniversários:", error);
      }
    }
  }, [user?.id, user?.companyId]);

  // Socket listeners
  useSocketListener(socket, user, 'auth', handleAuthEvent);
  useSocketListener(socket, user, 'user', handleUserUpdate);
  useSocketListener(socket, user, 'user-birthday', handleUserBirthday);
  useSocketListener(socket, user, 'contact-birthday', handleContactBirthday);

  // Verificar aniversários no login
  useEffect(() => {
    if (user?.id && user?.companyId) {
      const timer = setTimeout(() => {
        checkBirthdaysOnLogin();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);

  // Status do usuário
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");

      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  // Click outside handlers
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target) &&
        avatarRef.current &&
        !avatarRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target)
      ) {
        setShowOptions(false);
      }
      if (
        mobileMoreMenuRef.current &&
        !mobileMoreMenuRef.current.contains(event.target)
      ) {
        setMobileMoreMenuOpen(false);
      }
      if (
        desktopUtilityRef.current &&
        !desktopUtilityRef.current.contains(event.target)
      ) {
        setDesktopUtilityOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuRef, avatarRef, languageRef, mobileMoreMenuRef, desktopUtilityRef]);

  // Connection warning
  // FIX 11/06 #153: ignora whatsapps com isActive===false (intencionalmente
  // inativas) - nao alarmar como desconectadas.
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const activeWhats = whatsApps.filter((w) => w.isActive !== false);
      if (activeWhats.length > 0) {
        const offlineWhats = activeWhats.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  // Buscar configuração de limite de aviso
  useEffect(() => {
    const fetchWarningThreshold = async () => {
      if (user && user.companyId) {
        try {
          const { data } = await api.get("/companySettingOne", {
            params: { column: "diskQuotaWarningThreshold" }
          });
          if (data && data.value) {
            setWarningThreshold(parseInt(data.value, 10) || 90);
          }
        } catch (error) {
          // Ignorar erro, usar padrão de 90%
        }
      }
    };
    fetchWarningThreshold();
  }, [user]);

  // Disk usage - busca apenas uma vez ao carregar (atualizado via cron no backend a cada 30min)
  // Agora busca para todos os perfis, não apenas admin
  const fetchDiskUsage = useCallback(async () => {
    if (user && user.companyId) {
      try {
        const { data } = await api.get(
          `/companies/${user.companyId}/disk-usage`
        );
        // Garantir que diskQuota seja um número válido > 0
        if (data && (!data.diskQuota || data.diskQuota <= 0)) {
          console.warn("Disk quota inválido recebido:", data);
          // Se não tiver quota válida, não exibir o indicador
          setDiskUsage(null);
        } else {
          setDiskUsage(data);
        }
      } catch (error) {
        // Log do erro para debug
        console.error("Erro ao buscar uso de disco:", {
          status: error?.response?.status,
          message: error?.message,
          url: `/companies/${user.companyId}/disk-usage`
        });
        // Não definir diskUsage em caso de erro, para não exibir o indicador
        setDiskUsage(null);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchDiskUsage();
  }, [fetchDiskUsage]);

  // Verificar se atingiu o limite de aviso (90% por padrão) ou 100% e mostrar modal
  useEffect(() => {
    if (diskUsage && diskUsage.percentage >= warningThreshold && !hasShownWarning) {
      setShowDiskQuotaModal(true);
      setHasShownWarning(true);
    } else if (diskUsage && diskUsage.percentage < warningThreshold) {
      // Resetar flag quando voltar abaixo do limite
      setHasShownWarning(false);
    }
  }, [diskUsage, warningThreshold, hasShownWarning]);

  // Escutar evento para mostrar progresso de limpeza
  useEffect(() => {
    const handleShowCleanupProgress = (event) => {
      if (event.detail.companyId === user?.companyId) {
        setShowCleanupProgress(true);
      }
    };

    window.addEventListener('showCleanupProgress', handleShowCleanupProgress);
    return () => {
      window.removeEventListener('showCleanupProgress', handleShowCleanupProgress);
      // Limpar timeout de fechamento se existir
      if (cleanupCloseTimeoutRef.current) {
        clearTimeout(cleanupCloseTimeoutRef.current);
        cleanupCloseTimeoutRef.current = null;
      }
    };
  }, [user]);

  // Escutar evento de atualização de uso de disco via socket
  useEffect(() => {
    if (socket && user?.companyId) {
      const handleDiskUsageUpdate = (data) => {
        console.log("[ModernLayout] Evento diskUsageUpdated recebido:", data);
        if (data && data.companyId === user.companyId && data.diskUsage) {
          console.log("[ModernLayout] Atualizando uso de disco via socket:", {
            formattedDiskUsage: data.diskUsage.formattedDiskUsage,
            percentage: data.diskUsage.percentage,
            diskUsage: data.diskUsage.diskUsage,
            diskQuota: data.diskUsage.diskQuota
          });
          
          // Forçar atualização do estado
          setDiskUsage(prev => {
            console.log("[ModernLayout] Estado anterior:", prev);
            console.log("[ModernLayout] Novo estado:", data.diskUsage);
            return data.diskUsage;
          });
          
          // Resetar flag de aviso se voltou abaixo do limite
          if (data.diskUsage.percentage < warningThreshold) {
            setHasShownWarning(false);
          }
        } else {
          console.warn("[ModernLayout] Evento diskUsageUpdated recebido mas dados inválidos:", {
            data,
            hasCompanyId: !!data?.companyId,
            matchesCompanyId: data?.companyId === user.companyId,
            hasDiskUsage: !!data?.diskUsage
          });
        }
      };

      socket.on("diskUsageUpdated", handleDiskUsageUpdate);
      console.log("[ModernLayout] Listener diskUsageUpdated registrado para empresa:", user.companyId);
      
      // Log de debug: verificar se o socket está conectado
      if (socket.connected) {
        console.log("[ModernLayout] Socket está conectado e pronto para receber eventos");
      } else {
        console.warn("[ModernLayout] Socket NÃO está conectado!");
        socket.on("connect", () => {
          console.log("[ModernLayout] Socket conectado agora!");
        });
      }
      
      return () => {
        socket.off("diskUsageUpdated", handleDiskUsageUpdate);
        console.log("[ModernLayout] Listener diskUsageUpdated removido");
      };
    }
  }, [socket, user?.companyId, warningThreshold]);

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    setMenuOpen(false);
  };

  const handleClickLogout = () => {
    setMenuOpen(false);
    handleLogout();
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const closeMobileDrawer = () => {
    setMobileDrawerOpen(false);
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const handleUpdateStart = () => {
    setUpdateInProgress(true);
  };

  const handleUpdateComplete = () => {
    setUpdateInProgress(false);
  };

  // FIX 11/06 #153: usar apenas conexoes ativas (isActive !== false) no
  // contador e na classe de aviso.
  const activeWhatsApps = whatsApps.filter((w) => w.isActive !== false);

  const getConnectionStatusClass = () => {
    if (!connectionWarning) return "online";
    if (
      activeWhatsApps.some(
        (whats) => whats.status === "DISCONNECTED" || whats.status === "TIMEOUT"
      )
    ) {
      return "offline";
    }
    return "warning";
  };

  const getConnectionStatusText = () => {
    const disconnectedCount = activeWhatsApps.filter(
      (whats) =>
        whats.status === "DISCONNECTED" ||
        whats.status === "TIMEOUT" ||
        whats.status === "qrcode" ||
        whats.status === "PAIRING" ||
        whats.status === "OPENING"
    ).length;

    const connectedCount = activeWhatsApps.length - disconnectedCount;

    if (!connectionWarning)
      return `${activeWhatsApps.length}/${activeWhatsApps.length} ${i18n.t(
        "connections.online"
      )}`;

    if (
      activeWhatsApps.some(
        (whats) => whats.status === "DISCONNECTED" || whats.status === "TIMEOUT"
      )
    ) {
      return `${connectedCount}/${activeWhatsApps.length} ${i18n.t(
        "connections.offline"
      )}`;
    }

    return `${connectedCount}/${activeWhatsApps.length} ${i18n.t(
      "connections.warning"
    )}`;
  };

  const getQuotaBarColor = () => {
    if (!diskUsage || !diskUsage.diskQuota) return "success";

    // Usar percentage do diskUsage se disponível, senão calcular
    const percentage = diskUsage.percentage !== undefined 
      ? diskUsage.percentage 
      : Math.round((diskUsage.diskUsage / diskUsage.diskQuota) * 100);

    if (percentage > 85) return "error";
    if (percentage > 65) return "warning";
    return "success";
  };

  const getQuotaPercentage = () => {
    if (!diskUsage || !diskUsage.diskQuota) return 0;
    // Usar percentage do diskUsage se disponível, senão calcular
    const percentage = diskUsage.percentage !== undefined 
      ? diskUsage.percentage 
      : Math.round((diskUsage.diskUsage / diskUsage.diskQuota) * 100);
    return Math.min(percentage, 100);
  };

  if (loading || updateInProgress) {
    return <BackdropLoading />;
  }

  return (
    <div className={`app-container ${isDarkMode ? "dark" : "light"}`}>
      <header className="navbar">
        <div className="navbar-container">
          <div className="navbar-left">
            {!hideMenu && (
              <button
                className="mobile-menu-button"
                onClick={toggleMobileDrawer}
                aria-label="Menu"
              >
                <Menu className="icon" />
              </button>
            )}

            <div className="navbar-logo desktop-only">
              <Logo size="medium" />
            </div>

            {!hideMenu && (
              <div className="navbar-menu">
                <div className="desktop-menu">
                  <MainListItems />
                </div>
              </div>
            )}
          </div>

          <div className="navbar-actions">
            {!hideMenu && (
              <>
                <div className="version-container">
                  <VersionControl
                    onUpdateStart={handleUpdateStart}
                    onUpdateComplete={handleUpdateComplete}
                  />
                </div>

                {whatsApps.length > 0 && (
                  <Tooltip
                    title={
                      i18n.t("connections.title") + ": " + getConnectionStatusText()
                    }
                    placement="bottom"
                    arrow
                  >
                    <Link
                      to="/connections"
                      className="connection-status-container"
                      aria-label={i18n.t("connections.title")}
                      style={{ textDecoration: "none", cursor: "pointer", color: "inherit", display: "inline-flex" }}
                    >
                      <div
                        className={`connection-status-indicator ${getConnectionStatusClass()}`}
                      ></div>
                      <span
                        className={`connection-status-text ${getConnectionStatusClass()}`}
                      >
                        {getConnectionStatusText()}
                      </span>
                    </Link>
                  </Tooltip>
                )}

                {diskUsage &&
                  diskUsage.diskQuota > 0 && (
                    <Tooltip
                      title={`Uso de disco: ${diskUsage.formattedDiskUsage} de ${diskUsage.formattedDiskQuota}`}
                      placement="bottom"
                      arrow
                    >
                      <div className="quota-usage-container">
                        <span className="quota-usage-text">
                          <HardDrive
                            size={16}
                            className={`disk-icon ${getQuotaBarColor()}`}
                          />
                        </span>
                        <div className="quota-progress-root">
                          <div
                            className={`quota-progress-bar ${getQuotaBarColor()}`}
                            style={{ width: `${getQuotaPercentage()}%` }}
                          ></div>
                          <span className="quota-progress-text">
                            {getQuotaPercentage()}%
                          </span>
                        </div>
                      </div>
                    </Tooltip>
                  )}

                {/* Desktop: idioma, tema, atualizar, volume e sino em painel à esquerda (economiza espaço na barra) */}
                {!isMobile && (
                  <div className="desktop-utility-wrap" ref={desktopUtilityRef}>
                    {desktopUtilityOpen && (
                      <div className="desktop-utility-flyout" role="toolbar" aria-label="Ferramentas">
                        <div className="language-selector" ref={languageRef}>
                          <Tooltip title={i18n.t("mainDrawer.appBar.language") || "Idioma"} arrow>
                            <button
                              type="button"
                              onClick={() => setShowOptions(!showOptions)}
                              className="icon-button"
                            >
                              <FaGlobe size={18} />
                            </button>
                          </Tooltip>

                          {showOptions && (
                            <div className="language-dropdown-menu">
                              {filteredLanguageOptions.map((lang) => (
                                <button
                                  key={lang.code}
                                  type="button"
                                  onClick={() => handleLanguageChange(lang.code)}
                                >
                                  {lang.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="theme-toggle">
                          <IconButtonWithTooltip
                            icon={isDarkMode ? Sun : Moon}
                            tooltip={isDarkMode ? i18n.t("mainDrawer.appBar.theme.light") : i18n.t("mainDrawer.appBar.theme.dark")}
                            onClick={colorMode.toggleColorMode}
                            aria-label={isDarkMode ? i18n.t("mainDrawer.appBar.theme.light") : i18n.t("mainDrawer.appBar.theme.dark")}
                          />
                        </div>

                        <Tooltip title={i18n.t("mainDrawer.appBar.refresh")} arrow>
                          <button
                            type="button"
                            onClick={handleRefreshPage}
                            className="icon-button"
                            aria-label={i18n.t("mainDrawer.appBar.refresh")}
                          >
                            <RefreshCw size={18} />
                          </button>
                        </Tooltip>

                        <div className="notifications-volume">
                          <NotificationsVolume setVolume={setVolume} volume={volume} />
                        </div>

                        {user?.id && (
                          <Tooltip title="Notificações do navegador (alertas mesmo fora do sistema)" arrow>
                            <Link
                              to="/browser-notifications"
                              className="icon-button"
                              aria-label="Notificações push no navegador"
                              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={() => setDesktopUtilityOpen(false)}
                            >
                              <Bell size={18} />
                            </Link>
                          </Tooltip>
                        )}
                      </div>
                    )}

                    <Tooltip
                      title={
                        desktopUtilityOpen
                          ? "Fechar ferramentas"
                          : "Idioma, tema, atualizar, volume e notificações do navegador"
                      }
                      arrow
                    >
                      <button
                        type="button"
                        className="icon-button desktop-utility-toggle"
                        aria-expanded={desktopUtilityOpen}
                        aria-label={desktopUtilityOpen ? "Fechar menu de ferramentas" : "Abrir menu de ferramentas"}
                        onClick={() => setDesktopUtilityOpen((open) => !open)}
                      >
                        {desktopUtilityOpen ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                      </button>
                    </Tooltip>
                  </div>
                )}

                {/* Mobile: Menu "..." com opções colapsadas */}
                {isMobile && (
                  <div className="mobile-more-menu" ref={mobileMoreMenuRef}>
                    <Tooltip title="Mais opções" arrow>
                      <button
                        onClick={() => setMobileMoreMenuOpen(!mobileMoreMenuOpen)}
                        className="icon-button"
                      >
                        <MoreHorizontal size={18} />
                      </button>
                    </Tooltip>

                    {mobileMoreMenuOpen && (
                      <>
                        {/* Overlay para fechar ao clicar fora */}
                        <div 
                          className="mobile-more-overlay" 
                          onClick={() => setMobileMoreMenuOpen(false)} 
                        />
                        
                        <div className="mobile-more-dropdown">
                          {/* Tema */}
                          <button
                            className="mobile-more-item"
                            onClick={() => {
                              colorMode.toggleColorMode();
                              setMobileMoreMenuOpen(false);
                            }}
                          >
                            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                            <span>{isDarkMode ? "Modo Claro" : "Modo Escuro"}</span>
                          </button>

                          {/* Refresh */}
                          <button
                            className="mobile-more-item"
                            onClick={() => {
                              handleRefreshPage();
                              setMobileMoreMenuOpen(false);
                            }}
                          >
                            <RefreshCw size={18} />
                            <span>Atualizar</span>
                          </button>

                          {/* Idioma */}
                          <div className="mobile-more-section">
                            <span className="mobile-more-label">
                              <Globe size={14} /> Idioma
                            </span>
                            <div className="mobile-more-options">
                              {filteredLanguageOptions.map((lang) => (
                                <button
                                  key={lang.code}
                                  onClick={() => {
                                    handleLanguageChange(lang.code);
                                    setMobileMoreMenuOpen(false);
                                  }}
                                  className="mobile-more-option"
                                >
                                  {lang.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Som */}
                          <div className="mobile-more-section">
                            <span className="mobile-more-label">
                              <Volume2 size={14} /> Volume
                            </span>
                            <div className="mobile-more-volume">
                              <NotificationsVolume setVolume={setVolume} volume={volume} />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {isMobile && user?.id && (
                  <Tooltip title="Notificações do navegador (alertas mesmo fora do sistema)" arrow>
                    <Link
                      to="/browser-notifications"
                      className="icon-button"
                      aria-label="Notificações push no navegador"
                      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <Bell size={18} />
                    </Link>
                  </Tooltip>
                )}

                {user.id && (
                  <div className="notifications">
                    <NotificationsPopOver volume={volume} />
                  </div>
                )}

                <div className="announcements">
                  <AnnouncementsPopover />
                </div>

                <div className="chat-popover">
                  <ChatPopover />
                </div>

              </>
            )}

            <div className="user-profile">
              <Tooltip
                title={i18n.t("mainDrawer.appBar.user.profile") || "Perfil"}
                placement="bottom"
                arrow
              >
                <div
                  className="avatar-container"
                  ref={avatarRef}
                  onClick={toggleMenu}
                >
                  <div className="status-badge">
                    <Avatar 
                      alt={user.name}
                      src={!imageError ? profileUrl : undefined}
                      className="avatar"
                      onError={() => setImageError(true)}
                      style={{ 
                        width: 36, 
                        height: 36, 
                        fontSize: 14,
                        backgroundColor: imageError || !profileUrl ? "#7367f0" : undefined 
                      }}
                    >
                      {getInitials(user.name)}
                    </Avatar>
                  </div>
                </div>
              </Tooltip>
            </div>

            <UserModal
              open={userModalOpen}
              onClose={() => setUserModalOpen(false)}
              onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
              userId={user?.id}
            />

            {menuOpen && (
              <div className="user-menu" ref={userMenuRef}>
                <div className="user-menu-header">
                  <Avatar 
                    alt={user.name}
                    src={!imageError ? profileUrl : undefined}
                    className="user-menu-avatar"
                    style={{ 
                      width: 40, 
                      height: 40, 
                      fontSize: 16,
                      backgroundColor: imageError || !profileUrl ? "#7367f0" : undefined 
                    }}
                  >
                    {getInitials(user.name)}
                  </Avatar>
                  <div className="user-info">
                    <p className="user-name">{user.name}</p>
                    <p className="user-email">{user.email}</p>
                  </div>
                </div>
                <div className="menu-divider"></div>
                <button className="menu-item" onClick={handleOpenUserModal}>
                  <UserCircle className="menu-icon" />
                  <span>{i18n.t("mainDrawer.appBar.user.profile")}</span>
                </button>
                <button className="menu-item" onClick={handleClickLogout}>
                  <LogOut className="menu-icon" />
                  <span>{i18n.t("mainDrawer.appBar.user.logout")}</span>
                </button>
                <div className="menu-divider"></div>
                <button 
                  className="menu-version" 
                  onClick={() => {
                    setChangelogModalOpen(true);
                    setMenuOpen(false);
                  }}
                >
                  <Info className="menu-icon" />
                  <span>Versão {packageVersion}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Drawer mobile */}
      {!hideMenu && mobileDrawerOpen && (
        <div className="drawer-backdrop" onClick={toggleMobileDrawer}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-logo">
                <Logo size="medium" />
              </div>
              <button
                className="close-drawer-button"
                onClick={toggleMobileDrawer}
              >
                <X className="icon" />
              </button>
            </div>

            <div className="drawer-user">
              <div className="drawer-user-avatar">
                <Avatar 
                  alt={user.name}
                  src={!imageError ? profileUrl : undefined}
                  style={{ 
                    width: 60, 
                    height: 60, 
                    fontSize: 24,
                    backgroundColor: imageError || !profileUrl ? "#7367f0" : undefined 
                  }}
                >
                  {getInitials(user.name)}
                </Avatar>
              </div>
              <div className="drawer-user-info">
                <p className="drawer-user-name">{user.name}</p>
                <p className="drawer-user-email">{user.email}</p>
              </div>
            </div>

            <div className="menu-divider"></div>

            <div className="drawer-container">
              <MainListItems isMobile={true} closeDrawer={closeMobileDrawer} />
            </div>

            <div className="drawer-footer">
              <button
                className="drawer-footer-button"
                onClick={colorMode.toggleColorMode}
              >
                {isDarkMode ? (
                  <>
                    <Sun className="drawer-footer-icon" />
                    <span>{i18n.t("mainDrawer.appBar.theme.light") || "Modo Claro"}</span>
                  </>
                ) : (
                  <>
                    <Moon className="drawer-footer-icon" />
                    <span>{i18n.t("mainDrawer.appBar.theme.dark") || "Modo Escuro"}</span>
                  </>
                )}
              </button>

              <button
                className="drawer-footer-button"
                onClick={handleClickLogout}
              >
                <LogOut className="drawer-footer-icon" />
                <span>{i18n.t("mainDrawer.appBar.user.logout")}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="main-content">
        <div className="content-container">{children}</div>
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                {selectedAnnouncement.text}
              </Typography>
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              )}
              <Button
                onClick={() => setSelectedAnnouncement(null)}
                style={{ marginTop: 16 }}
                variant="outlined"
              >
                Voltar para lista
              </Button>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <NotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          Prioridade: {announcement.priority === 1 ? 'Alta' : announcement.priority === 2 ? 'Média' : 'Baixa'}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAnnouncementsModal(false)}
            color="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aniversário */}
      <BirthdayModal
        open={showBirthdayModal}
        onClose={() => setShowBirthdayModal(false)}
        user={user}
      />

      {/* Modal de Changelog/Atualizações */}
      <Dialog
        open={changelogModalOpen}
        onClose={() => setChangelogModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            height: "80vh",
            maxHeight: "700px",
            borderRadius: "12px",
          }
        }}
      >
        <DialogTitle style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: "12px 16px"
        }}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            📋 Atualizações do Sistema - v{packageVersion}
          </Typography>
          <Button 
            onClick={() => setChangelogModalOpen(false)}
            style={{ minWidth: "auto", padding: "4px" }}
          >
            <X size={20} />
          </Button>
        </DialogTitle>
        <DialogContent style={{ padding: 0, overflow: "hidden" }}>
          <iframe
            src={getEmbedUrl(changelogUrl)}
            title="Changelog"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de limite de disco */}
      {user && user.companyId && (
        <>
          <DiskQuotaLimitModal
            open={showDiskQuotaModal}
            onClose={() => setShowDiskQuotaModal(false)}
            companyId={user.companyId}
            diskUsage={diskUsage}
            diskQuota={diskUsage?.diskQuota}
            isBlocked={diskUsage?.percentage >= 100}
          />
          {showCleanupProgress && (
            <DiskCleanupProgress
              companyId={user.companyId}
              onComplete={(data) => {
                console.log("[ModernLayout] onComplete chamado com:", data);
                
                // Limpar timeout anterior se existir
                if (cleanupCloseTimeoutRef.current) {
                  clearTimeout(cleanupCloseTimeoutRef.current);
                  cleanupCloseTimeoutRef.current = null;
                }
                
                // Garantir que o modal será fechado
                const closeModal = () => {
                  console.log("[ModernLayout] Fechando modal de limpeza");
                  setShowCleanupProgress(false);
                  if (cleanupCloseTimeoutRef.current) {
                    clearTimeout(cleanupCloseTimeoutRef.current);
                    cleanupCloseTimeoutRef.current = null;
                  }
                };
                
                // Atualizar uso de disco após limpeza
                if (data && data.status === "completed") {
                  // Atualizar uso de disco imediatamente
                  console.log("[ModernLayout] Atualizando uso de disco após limpeza...");
                  fetchDiskUsage();
                  
                  // Tentar atualizar múltiplas vezes para garantir que pegue o valor atualizado
                  setTimeout(() => {
                    console.log("[ModernLayout] Tentativa 1 de atualizar uso de disco");
                    fetchDiskUsage();
                  }, 500);
                  
                  setTimeout(() => {
                    console.log("[ModernLayout] Tentativa 2 de atualizar uso de disco");
                    fetchDiskUsage();
                  }, 1500);
                  
                  setTimeout(() => {
                    console.log("[ModernLayout] Tentativa 3 de atualizar uso de disco");
                    fetchDiskUsage();
                  }, 3000);
                  
                  // Mostrar toast de sucesso
                  toast.success(`Limpeza concluída! ${data.filesDeleted || 0} arquivos deletados, ${formatBytes(data.spaceFreed || 0)} liberados.`);
                  
                  // Fechar modal após 1.5 segundos para dar tempo de ver o resultado
                  cleanupCloseTimeoutRef.current = setTimeout(closeModal, 1500);
                } else if (data && data.status === "error") {
                  toast.error(`Erro na limpeza: ${data.error || "Erro desconhecido"}`);
                  cleanupCloseTimeoutRef.current = setTimeout(closeModal, 2000);
                } else {
                  // Se não tiver status claro, fechar mesmo assim após 2 segundos
                  console.warn("[ModernLayout] onComplete chamado sem status claro, fechando modal");
                  cleanupCloseTimeoutRef.current = setTimeout(closeModal, 2000);
                }
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ModernLayout;
